// @ts-nocheck
/**
 * Notifications Tab - Configurações de Notificações
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/state/session";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export function NotificationsTab() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data: config, isLoading } = useQuery({
    queryKey: ["user-notifications", userId],
    queryFn: async () => {
      if (!userId) throw new Error("Usuário não autenticado");
      
      let { data, error } = await supabase
        .from("configuracoes_usuario")
        .select("*")
        .eq("user_id" as any, userId)
        .maybeSingle() as any;

      if (error) throw error;
      
      if (!data) {
        const { data: newConfig, error: insertError } = await supabase
          .from("configuracoes_usuario")
          .insert({ user_id: userId } as any)
          .select()
          .single() as any;
        
        if (insertError) throw insertError;
        data = newConfig;
      }

      return data;
    },
    enabled: !!userId,
  });

  const updateMutation = useMutation({
    mutationFn: async (values: any) => {
      const { error } = await supabase
        .from("configuracoes_usuario")
        .update({
          ...values,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("user_id" as any, userId) as any;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-notifications", userId] });
      toast.success("✅ Notificações atualizadas!");
    },
    onError: () => {
      toast.error("❌ Erro ao atualizar notificações");
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const handleToggle = (field: string, value: boolean) => {
    updateMutation.mutate({ [field]: value });
  };

  const handleCanalChange = (value: string) => {
    updateMutation.mutate({ notif_canal_preferido: value });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>🔔 Notificações</CardTitle>
        <CardDescription>
          Configure como e quando você quer ser notificado
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Aviso de estoque baixo</Label>
            <p className="text-xs text-muted-foreground">
              Receba um alerta quando algum produto estiver acabando
            </p>
          </div>
          <Switch
            checked={config?.notif_estoque_baixo}
            onCheckedChange={(checked) => handleToggle("notif_estoque_baixo", checked)}
            disabled={updateMutation.isPending}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Aviso de venda pendente</Label>
            <p className="text-xs text-muted-foreground">
              Exibir no painel inicial quando houver vendas pendentes
            </p>
          </div>
          <Switch
            checked={config?.notif_venda_pendente}
            onCheckedChange={(checked) => handleToggle("notif_venda_pendente", checked)}
            disabled={updateMutation.isPending}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Resumo diário por e-mail</Label>
            <p className="text-xs text-muted-foreground">
              Receba um relatório das vendas do dia toda noite
            </p>
          </div>
          <Switch
            checked={config?.notif_resumo_diario}
            onCheckedChange={(checked) => handleToggle("notif_resumo_diario", checked)}
            disabled={updateMutation.isPending}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Som de alerta</Label>
            <p className="text-xs text-muted-foreground">
              Toque sonoro para notificações importantes
            </p>
          </div>
          <Switch
            checked={config?.notif_som_alerta}
            onCheckedChange={(checked) => handleToggle("notif_som_alerta", checked)}
            disabled={updateMutation.isPending}
          />
        </div>

        <div className="space-y-2">
          <Label>Canal preferido</Label>
          <Select
            value={config?.notif_canal_preferido}
            onValueChange={handleCanalChange}
            disabled={updateMutation.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="email">📧 E-mail</SelectItem>
              <SelectItem value="whatsapp">💬 WhatsApp</SelectItem>
              <SelectItem value="push">🔔 Notificação Push</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Onde você prefere receber alertas importantes
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
